import React, { useEffect, useState, useRef } from 'react';
import { UnifiedMessage, WhatsAppLead } from '../../types/whatsapp';
import { MessageItem } from './MessageItem';
import { SendIcon, PaperclipIcon, SmileIcon, PhoneIcon, MoreVerticalIcon, MessageCircleIcon, MessageSquareIcon, ChevronDownIcon } from 'lucide-react';
interface UnifiedThreadProps {
  lead: WhatsAppLead;
  messages: UnifiedMessage[];
  onSendMessage: (content: string, channel: 'whatsapp' | 'sms') => void;
}
export function UnifiedThread({
  lead,
  messages,
  onSendMessage
}: UnifiedThreadProps) {
  const [inputMessage, setInputMessage] = useState('');
  const [activeChannel, setActiveChannel] = useState<'whatsapp' | 'sms'>('whatsapp');
  const [showChannelDropdown, setShowChannelDropdown] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowChannelDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSend = () => {
    if (!inputMessage.trim()) return;
    onSendMessage(inputMessage, activeChannel);
    setInputMessage('');
  };
  return <div className="flex flex-col h-full bg-[#f0f2f5]" data-id="element-167">
      {/* Header */}
      <div className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm z-10" data-id="element-168">
        <div className="flex items-center space-x-3" data-id="element-169">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-lg" data-id="element-170">
            {lead.name.charAt(0)}
          </div>
          <div data-id="element-171">
            <h3 className="font-semibold text-gray-900" data-id="element-172">{lead.name}</h3>
            <div className="flex items-center text-xs text-gray-500 space-x-2" data-id="element-173">
              <span data-id="element-174">{lead.phone}</span>
              <span data-id="element-175">•</span>
              <span className="text-green-600 font-medium" data-id="element-176">Online</span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2" data-id="element-177">
          <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" title="Call" data-id="element-178">
            <PhoneIcon size={20} data-id="element-179" />
          </button>
          <button className="p-2 text-gray-500 hover:bg-gray-100 rounded-full transition-colors" data-id="element-180">
            <MoreVerticalIcon size={20} data-id="element-181" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-6 bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat" data-id="element-182">
        {messages.map(msg => <MessageItem key={msg.id} message={msg} data-id="element-183" />)}
        <div ref={messagesEndRef} data-id="element-184" />
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 p-4" data-id="element-185">
        <div className="flex items-end space-x-2 bg-gray-50 p-2 rounded-xl border border-gray-200" data-id="element-186">
          {/* Channel Selector Dropdown */}
          <div className="relative" ref={dropdownRef} data-id="element-187">
            <button onClick={() => setShowChannelDropdown(!showChannelDropdown)} className={`flex items-center space-x-2 px-3 py-2 rounded-lg border transition-all ${activeChannel === 'whatsapp' ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100' : 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'}`} data-id="element-188">
              {activeChannel === 'whatsapp' ? <MessageCircleIcon size={18} data-id="element-189" /> : <MessageSquareIcon size={18} data-id="element-190" />}
              <ChevronDownIcon size={16} data-id="element-191" />
            </button>

            {/* Dropdown Menu */}
            {showChannelDropdown && <div className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10" data-id="element-192">
                <button onClick={() => {
              setActiveChannel('whatsapp');
              setShowChannelDropdown(false);
            }} className={`w-full flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors ${activeChannel === 'whatsapp' ? 'bg-green-50 text-green-700' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-193">
                  <MessageCircleIcon size={18} className="text-green-600" data-id="element-194" />
                  <span className="font-medium" data-id="element-195">WhatsApp</span>
                </button>
                <button onClick={() => {
              setActiveChannel('sms');
              setShowChannelDropdown(false);
            }} className={`w-full flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors ${activeChannel === 'sms' ? 'bg-purple-50 text-purple-700' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-196">
                  <MessageSquareIcon size={18} className="text-purple-600" data-id="element-197" />
                  <span className="font-medium" data-id="element-198">SMS</span>
                </button>
              </div>}
          </div>

          <button className="p-2 text-gray-500 hover:bg-gray-200 rounded-full transition-colors" data-id="element-199">
            <SmileIcon size={20} data-id="element-200" />
          </button>
          <button className="p-2 text-gray-500 hover:bg-gray-200 rounded-full transition-colors" data-id="element-201">
            <PaperclipIcon size={20} data-id="element-202" />
          </button>
          <textarea value={inputMessage} onChange={e => setInputMessage(e.target.value)} onKeyDown={e => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
          }
        }} placeholder={`Type a message...`} className="flex-1 bg-transparent border-0 focus:ring-0 resize-none max-h-32 py-2 text-gray-900 placeholder-gray-400" rows={1} data-id="element-203" />
          <button onClick={handleSend} disabled={!inputMessage.trim()} className={`p-2 rounded-full transition-all transform hover:scale-105 ${inputMessage.trim() ? activeChannel === 'whatsapp' ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-gray-200 text-gray-400'}`} data-id="element-204">
            <SendIcon size={20} data-id="element-205" />
          </button>
        </div>
        <div className="flex justify-between items-center mt-2 px-1" data-id="element-206">
          <span className="text-xs text-gray-400" data-id="element-207">
            Press Enter to send, Shift+Enter for new line
          </span>
          <button className="text-xs text-blue-600 font-medium hover:underline" data-id="element-208">
            Use Template
          </button>
        </div>
      </div>
    </div>;
}